import React, {useEffect, useState, useRef} from 'react';
import {View, Text, Image, Pressable, ActivityIndicator} from 'react-native';
import moment from 'moment';
import {
  usePDF,
  BlobProvider,
  Document,
  View as PDFView,
  Page,
  Text as PDFText,
  Image as PDFImage,
  pdf,
} from '@react-pdf/renderer';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';

import {IAttachmentService} from '../../Services/Interfaces/IAttachmentService';
import AttachmentService from '../../Services/AttachmentService';
import LoadingSpinner from '../Shared/LoadingSpinner';
import Icon from '../Shared/Icon';
import {
  Submission,
  SubmissionBundle_submissions,
} from '../../Models/PowerSyncModels/models';
import {DynamicAttachment} from '../../Types/ControlTypes';
import {Formats} from '../../Constants/Formats';
import {METADATA_KEYS} from '../../Constants/AppConstants';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import {Images} from '../../Constants/Images';
import Colors from '../../Styles/Shared/Colors';
import {PDFStyles, FileUrl} from './DynamicPDF';

type DynamicBundlePDFProps = {
  bundleId: string;
  index?: number;
  addUrls?: (urls: FileUrl[]) => void;
};

const DynamicBundlePDF = (props: DynamicBundlePDFProps): React.ReactElement => {
  const attachmentService: IAttachmentService = new AttachmentService(() => {});

  const [orgName, setOrgName] = useState('');
  const [orgLogo, setOrgLogo] = useState('');
  const [documentTitle, setDocumentTitle] = useState('');
  const [date, setDate] = useState('');
  const [isAudit, setIsAudit] = useState(false);
  const [auditHeader, setAuditHeader] = useState('');
  const [internalRecipients, setInternalRecipients] = useState('');
  const [externalOrg, setExternalOrg] = useState('');
  const [externalRecipients, setExternalRecipients] = useState('');
  const [documents, setDocuments] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<DynamicAttachment[]>([]);
  const [isMounted, setIsMounted] = useState(false);

  const hasPDFAttachments = useRef(false);

  const [blobDocument, setBlobDocument] = useState<Blob | undefined>(undefined);

  const {
    submissionBundles,
    organisations,
    submissions,
    templateVersions,
    people,
    orgContractorNotificationRecipients,
  } = usePowerSyncData();

  useEffect(() => {
    const getBundle = async () => {
      if (props.bundleId) {
        let realmBundle = submissionBundles.find(
          s => s._id === props.bundleId!,
        );
        if (realmBundle) {
          let org = organisations.find(
            o => o.SQLServerId === realmBundle!.partition,
          );
          if (org) {
            if (org.name) setOrgName(org.name);

            if (org.OrgTagPublic) {
              let uriLogo =
                process.env.REACT_APP_ORG_LOGO_REPO +
                org.OrgTagPublic +
                '/logo.png';

              let logoExists =
                await attachmentService.checkIfLogoExists(uriLogo);

              if (logoExists) setOrgLogo(uriLogo);
            }
          }

          setDocumentTitle(realmBundle.name ?? '');

          if (realmBundle.submitDateTimeStamp)
            setDate(
              moment(realmBundle.submitDateTimeStamp).format(
                Formats.FRONTEND_DATE,
              ),
            );

          if (realmBundle.isAudit) {
            setIsAudit(realmBundle.isAudit);
            let auditSubmission = submissions.find(
              x =>
                realmBundle!.auditSubmissionId &&
                x._id === realmBundle!.auditSubmissionId,
            );

            if (auditSubmission)
              setAuditHeader(
                await getReportTitle(auditSubmission as Submission),
              );
          }

          let intRec = '';
          if (realmBundle.internalRecipients) {
            let intRecipients = people.filter(
              r =>
                r.email &&
                realmBundle!.internalRecipients!.indexOf(r.email) != -1,
            );

            intRecipients.forEach((r, i) => {
              intRec += (i === 0 ? '' : ', ') + r.firstName + ' ' + r.lastName;
            });
          }
          setInternalRecipients(intRec);

          if (realmBundle.externalOrgId) {
            let extOrgObj = organisations.find(
              o => o.SQLServerId === realmBundle!.externalOrgId,
            );
            if (extOrgObj) setExternalOrg(extOrgObj.name);
          }

          let extRec = '';
          if (realmBundle.externalRecipients) {
            let extRecipients = orgContractorNotificationRecipients.filter(
              r =>
                r.email &&
                realmBundle!.externalRecipients!.indexOf(r.email) != -1,
            );

            extRecipients.forEach((r, i) => {
              extRec += (i === 0 ? '' : ', ') + r.firstName + ' ' + r.lastName;
            });
          }
          setExternalRecipients(extRec);

          let docs: string[] = [];
          if (realmBundle.submissions)
            for (let i = 0; i < realmBundle.submissions.length; i++) {
              docs.push(
                await getReportTitle(
                  realmBundle.submissions[i] as SubmissionBundle_submissions,
                ),
              );
            }
          setDocuments(docs);

          // Get Attachments
          let newAttachments: DynamicAttachment[] = [];
          if (realmBundle.bundleAttachments) {
            let attachmentsList: DynamicAttachment[] = [];

            try {
              attachmentsList = JSON.parse(
                realmBundle.bundleAttachments,
              ) as DynamicAttachment[];
              attachmentsList = attachmentsList.filter(x => x.blobUri);
            } catch (err) {
              console.log('Error: Bad control value serialization');
            }

            for (let i = 0; i < attachmentsList.length; i++) {
              try {
                let uri = await attachmentService.getSasUri(
                  attachmentsList[i].blobUri!,
                );
                attachmentsList[i].uri = uri;

                if (attachmentsList[i].type?.includes('pdf'))
                  hasPDFAttachments.current = true;
              } catch (err) {
                console.log('Error: Failed getting sasuri');
              }
            }

            newAttachments = [...attachmentsList];
          }
          setAttachments(newAttachments);

          setIsMounted(true);
        }
      }
    };

    getBundle();
  }, [props.bundleId]);

  useEffect(() => {
    if (isMounted && orgName && documentTitle && date) {
      const getPdfBlob = async () => {
        let blob = await pdf(renderPDF()).toBlob();
        setBlobDocument(blob);
      };

      getPdfBlob();
    }
  }, [isMounted, orgName, documentTitle, date]);

  useEffect(() => {
    addUrls();
  }, [blobDocument]);

  async function getReportTitle(
    submission: SubmissionBundle_submissions | Submission,
  ): Promise<string> {
    let template = templateVersions.find(
      x => x._id === submission!.templateVersion!._id!,
    )!;

    let title = template.name ?? '';
    if (submission.metadataJSON) {
      let meta = JSON.parse(submission.metadataJSON);
      if (meta[METADATA_KEYS.TITLE]) title = meta[METADATA_KEYS.TITLE];
      if (meta[METADATA_KEYS.PROJECT_NAME])
        title += ' at ' + meta[METADATA_KEYS.PROJECT_NAME];
    }

    let date = '';
    let submissionDate =
      submission.updateDateTimeStamp ?? submission.createDateTimeStamp;
    if (submissionDate)
      date = moment(submissionDate).format(Formats.FRONTEND_DATE);

    return title + ' - ' + date;
  }

  async function addUrls() {
    if (props.addUrls) {
      let urls: FileUrl[] = [];

      if (blobDocument)
        urls.push({
          index: props.index,
          name: documentTitle.replace(/[^\w\s]/g, '') + '.pdf',
          url: URL.createObjectURL(blobDocument),
        });

      if (attachments) {
        for (let i = 0; i < attachments.length; i++) {
          if (!attachments[i].type?.includes('image')) {
            if (
              attachments[i] &&
              attachments[i].blobUri &&
              attachments[i].name
            ) {
              let sasUri = await attachmentService.getSasUri(
                attachments[i].blobUri!,
              );

              urls.push({
                index: props.index,
                name: attachments[i].name!,
                url: sasUri,
              });
            }
          }
        }
      }

      props.addUrls(urls);
    }
  }

  async function downloadDocument() {
    if (blobDocument) {
      var element = document.createElement('a');
      element.href = URL.createObjectURL(blobDocument);
      element.download = documentTitle + '.pdf';
      element.click();
    }
  }

  async function downloadAttachment(
    attachment: DynamicAttachment,
  ): Promise<void> {
    if (attachment && attachment.blobUri && attachment.name)
      await attachmentService.downloadAttachment(
        attachment.blobUri,
        attachment.name,
      );
  }

  async function downloadReport(url: string, fileName: string) {
    var xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.open('GET', url, true);

    xhr.onreadystatechange = function () {
      if (xhr.readyState == xhr.DONE) {
        var blob = new Blob([xhr.response]);

        var element = document.createElement('a');
        element.href = URL.createObjectURL(blob);
        element.download = fileName;
        element.click();
      }
    };

    xhr.send();
  }

  const renderPDF = (): React.ReactElement => {
    return (
      <Document title={documentTitle}>
        {renderDocument()}
        {attachments.map(attachment => {
          return <>{renderAttachment(attachment)}</>;
        })}
      </Document>
    );
  };

  const renderCover = (): React.ReactElement => {
    return (
      <PDFView>
        <PDFView
          style={{
            flexDirection: 'row',
            paddingHorizontal: 24,
            paddingBottom: 10,
          }}>
          <PDFView style={{flex: 1, alignItems: 'flex-start'}}>
            <PDFImage
              source={orgLogo ? orgLogo : '/img/FIRS.png'}
              style={{height: 46}}
            />
          </PDFView>
          <PDFView
            style={{
              flex: 1,
              flexDirection: 'column',
              alignItems: 'flex-end',
              justifyContent: 'center',
            }}>
            <PDFText style={PDFStyles.templateType}>Submission Bundle</PDFText>
          </PDFView>
        </PDFView>
        <PDFView
          style={{
            borderTopWidth: 3,
            borderBottomWidth: 3,
            borderColor: '#2b5a38',
          }}>
          <PDFView
            style={{
              borderTopWidth: 2,
              borderBottomWidth: 2,
              borderColor: Colors.yellow,
            }}>
            <PDFView
              style={{
                borderTopWidth: 3,
                borderBottomWidth: 3,
                borderColor: Colors.red,
              }}>
              <PDFView
                style={{
                  backgroundColor: Colors.white,
                  textAlign: 'center',
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                }}>
                <PDFText style={PDFStyles.reportTitle}>{documentTitle}</PDFText>
                <PDFText style={PDFStyles.reportSubTitle}>{orgName}</PDFText>
                <PDFText style={PDFStyles.reportSubTitle}>{date}</PDFText>
              </PDFView>
            </PDFView>
          </PDFView>
        </PDFView>
      </PDFView>
    );
  };

  const renderDocument = (): React.ReactElement => {
    return (
      <Page size='A4' style={{paddingTop: 22, paddingBottom: 44}}>
        {renderCover()}
        <PDFView
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            paddingHorizontal: 24,
          }}>
          <PDFView
            style={{flexBasis: '100%', paddingVertical: 6, paddingRight: 6}}
            wrap={false}>
            <PDFText style={PDFStyles.label}>
              IS THIS AN AUDIT SUBMISSION BUNDLE?
            </PDFText>
            <PDFText style={PDFStyles.value}>{isAudit ? 'Yes' : 'No'}</PDFText>
          </PDFView>
          {isAudit && auditHeader && (
            <PDFView
              style={{flexBasis: '100%', paddingVertical: 6, paddingRight: 6}}
              wrap={false}>
              <PDFText style={PDFStyles.label}>AUDIT HEADER</PDFText>
              <PDFText style={PDFStyles.value}>{auditHeader}</PDFText>
              <PDFText style={[PDFStyles.value, {fontSize: 10}]}>
                *Audit header will be printed on a separated file.
              </PDFText>
            </PDFView>
          )}
          {internalRecipients && (
            <PDFView
              style={{flexBasis: '100%', paddingVertical: 6, paddingRight: 6}}
              wrap={false}>
              <PDFText style={PDFStyles.label}>INTERNAL RECIPIENTS</PDFText>
              <PDFText style={PDFStyles.value}>{internalRecipients}</PDFText>
            </PDFView>
          )}
          {externalOrg && (
            <PDFView
              style={{flexBasis: '100%', paddingVertical: 6, paddingRight: 6}}
              wrap={false}>
              <PDFText style={PDFStyles.label}>EXTERNAL ORGANISATION</PDFText>
              <PDFText style={PDFStyles.value}>{externalOrg}</PDFText>
            </PDFView>
          )}
          {externalRecipients && (
            <PDFView
              style={{flexBasis: '100%', paddingVertical: 6, paddingRight: 6}}
              wrap={false}>
              <PDFText style={PDFStyles.label}>EXTERNAL RECIPIENTS</PDFText>
              <PDFText style={PDFStyles.value}>{externalRecipients}</PDFText>
            </PDFView>
          )}
          {documents && documents.length > 0 && (
            <PDFView
              style={{flexBasis: '100%', paddingVertical: 6, paddingRight: 6}}
              wrap={false}>
              <PDFText style={PDFStyles.label}>DOCUMENTS INCLUDED</PDFText>
              {documents.map((docTitle, i) => {
                return (
                  <PDFText key={i} style={PDFStyles.value}>
                    {docTitle}
                  </PDFText>
                );
              })}
              <PDFText style={[PDFStyles.value, {fontSize: 10}]}>
                *Documents included will be printed on separated files.
              </PDFText>
            </PDFView>
          )}
        </PDFView>
        <PDFView
          fixed
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            position: 'absolute',
            bottom: 0,
            right: 0,
            paddingHorizontal: 24,
            paddingBottom: 6,
          }}>
          <PDFText style={PDFStyles.footerText}>Created with</PDFText>
          <PDFImage source={'/img/FIRS.png'} style={{height: 26}} />
        </PDFView>
      </Page>
    );
  };

  const renderAttachment = (
    attachment: DynamicAttachment,
  ): React.ReactElement => {
    return (
      <>
        {attachment.type?.includes('image') && attachment.uri ? (
          <Page size='A4' style={{paddingTop: 22, paddingBottom: 44}}>
            <PDFView
              style={{
                flex: 1,
                paddingHorizontal: 24,
              }}>
              <PDFText style={[PDFStyles.label, {marginBottom: 12}]}>
                {attachment.name}
              </PDFText>
              <PDFImage
                style={{objectFit: 'contain', maxHeight: 600}}
                source={attachment.uri}
              />
            </PDFView>
            <PDFView
              fixed
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                position: 'absolute',
                bottom: 0,
                right: 0,
                paddingHorizontal: 24,
                paddingBottom: 6,
              }}>
              <PDFText style={PDFStyles.footerText}>Created with</PDFText>
              <PDFImage source={'/img/FIRS.png'} style={{height: 26}} />
            </PDFView>
          </Page>
        ) : null}
      </>
    );
  };

  return (
    <>
      {isMounted ? (
        <>
          <View style={ControlsStyleSheet.attachmentsItem}>
            <Image style={{width: 32, height: 32}} source={Images.PDF} />
            <Text style={ControlsStyleSheet.attachmentsText}>
              {documentTitle}.pdf
            </Text>
            <View style={ControlsStyleSheet.attachmentsActions}>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.attachmentsAction,
                  pressed && {
                    backgroundColor: Colors.lightTeal,
                  },
                ]}
                onPress={() => downloadDocument()}>
                <Icon icon={'download'} color={Colors.green} size={20} />
              </Pressable>
            </View>
          </View>
          {hasPDFAttachments.current && (
            <>
              {attachments.map((attachment, i) => {
                if (attachment.type?.includes('image')) return <></>;
                return (
                  <View key={i} style={ControlsStyleSheet.attachmentsItem}>
                    {attachment.type?.includes('pdf') ? (
                      <Image
                        style={{width: 32, height: 32}}
                        source={Images.PDF}
                      />
                    ) : (
                      <Icon icon={'file'} color={Colors.green} size={20} />
                    )}
                    <Text style={ControlsStyleSheet.attachmentsText}>
                      {attachment.name}
                    </Text>
                    <View style={ControlsStyleSheet.attachmentsActions}>
                      <Pressable
                        style={({pressed}) => [
                          ControlsStyleSheet.attachmentsAction,
                          pressed && {
                            backgroundColor: Colors.lightTeal,
                          },
                        ]}
                        onPress={() => downloadAttachment(attachment)}>
                        <Icon
                          icon={'download'}
                          color={Colors.green}
                          size={20}
                        />
                      </Pressable>
                    </View>
                  </View>
                );
              })}
            </>
          )}
        </>
      ) : (
        <LoadingSpinner message='Generating Documents...' visible={true} />
      )}
    </>
  );
};

export default DynamicBundlePDF;
